import { memo, useCallback } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';

import { EditableCanvasSharePrivateLink } from '~/components/SharePrivateCanvasModal/EditableCanvasSharePrivateLink';
import { useUpdateShareCanvasLink } from '~/components/SharePrivateCanvasModal/hooks/useUpdateShareCanvasLink';
import { EnableSharedLinkToggle } from '~/components/SharePrivateContentModal/EnableSharedLinkToggle';
import { SharePrivateContentExpirationDateSetting } from '~/components/SharePrivateContentModal/SharePrivateContentAdvancedBoardSettings/SharePrivateContentExpirationDateSetting';
import { SharePrivateContentPasscodeProtection } from '~/components/SharePrivateContentModal/SharePrivateContentAdvancedBoardSettings/SharePrivateContentPasscodeProtection';
import { ShareRequireAccountSetting } from '~/components/SharePrivateContentModal/SharePrivateContentSettings/ShareRequireAccountSetting';

export type SharePrivateCanvasLinkInfoProps = {
  shortIdInfo: ShortUrlResponse;
  canvasId: string;
  trackLocation: AirActionTrackingLocation;
};

export const SharePrivateCanvasLinkInfo = memo(
  ({ shortIdInfo, canvasId, trackLocation }: SharePrivateCanvasLinkInfoProps) => {
    const { onShortUrlUpdate, isLoading } = useUpdateShareCanvasLink({ canvasId, shortIdInfo });

    const onToggleEnableLink = useCallback(
      (enabled: boolean) => onShortUrlUpdate({ disabled: !enabled }),
      [onShortUrlUpdate],
    );

    return (
      <div className="flex flex-col">
        <div className="flex flex-col gap-2 px-8 py-6">
          <div className="py-2">
            <EnableSharedLinkToggle
              enabled={!shortIdInfo.disabled}
              isLoading={isLoading}
              onToggle={onToggleEnableLink}
            />
          </div>
          {!shortIdInfo.disabled && (
            <>
              <div className="py-2">
                <ShareRequireAccountSetting
                  shortIdInfo={shortIdInfo}
                  isLoading={isLoading}
                  onShortUrlUpdate={onShortUrlUpdate}
                />
              </div>
              <div className="py-2">
                <SharePrivateContentPasscodeProtection
                  shortIdInfo={shortIdInfo}
                  onChangePasscode={(passcode) => onShortUrlUpdate({ passcode })}
                />
              </div>
              <div className="py-2">
                <SharePrivateContentExpirationDateSetting
                  shortIdInfo={shortIdInfo}
                  onChangeExpirationDate={(expirationDate) => onShortUrlUpdate({ expirationDate })}
                />
              </div>
            </>
          )}
        </div>
        {!shortIdInfo.disabled && (
          <div className="border-t border-t-grey-5 px-8 py-6">
            <EditableCanvasSharePrivateLink
              canvasId={canvasId}
              trackLocation={trackLocation}
              shortIdInfo={shortIdInfo}
            />
          </div>
        )}
      </div>
    );
  },
);

SharePrivateCanvasLinkInfo.displayName = 'SharePrivateCanvasLinkInfo';
